import React, { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { supabase, isSupabaseConfigured, proxyStorageUrl } from '../../lib/supabase'
import { registerServiceWorker, requestNotificationPermission, shouldAskPermission, showBrowserNotification, getPermissionStatus, isChatMuted } from '../../lib/notifications'
import { getSocket } from '../../lib/socketClient'
import { useTour } from '../../contexts/TourContext'
import { initTracker, stopTracker, trackPageView } from '../../lib/userActivity'
import Modal from '../Modal'
import TutorialModal from '../TutorialModal'
import CustomSelect from '../CustomSelect/CustomSelect'
import { getPermission, SECTION_ACCESS_MAP } from '../../lib/permissionDefaults'
import LogoIcon from '../LogoIcon/LogoIcon'
import NotificationBell from '../Notifications/NotificationBell'
import './AppLayout.css'

// Import page icons as inline SVG components
const icons = {
  dashboard: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>
    </svg>
  ),
  chat: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  acceptance: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/>
    </svg>
  ),
  returns: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  issued: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  ),
  schedule: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  payments: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  settings: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  violations: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  logout: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  menu: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  moon: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
  sun: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  ai: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a2 2 0 0 1 2 2c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2z"/>
      <path d="M19 9a2 2 0 0 1-2-2 2 2 0 0 1 2-2 2 2 0 0 1 2 2 2 2 0 0 1-2 2z"/>
      <path d="M5 9a2 2 0 0 1-2-2 2 2 0 0 1 2-2 2 2 0 0 1 2 2 2 2 0 0 1-2 2z"/>
      <rect x="7" y="11" width="10" height="10" rx="2"/>
      <path d="M12 11v10"/>
      <path d="M7 16h10"/>
    </svg>
  ),
  deductions: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18"/>
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
      <line x1="10" y1="11" x2="10" y2="17"/>
      <line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
  ),
  analytics: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>
    </svg>
  ),
  help: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  moon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
  sun: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  switchUser: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 3h5v5"/><path d="M8 21H3v-5"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/>
    </svg>
  ),
  tasks: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="1"/>
      <path d="M9 14l2 2 4-4"/>
    </svg>
  ),
  cameras: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 7l-7 5 7 5V7z"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  ),
  vacancies: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="8.5" cy="7" r="4"/>
      <line x1="20" y1="8" x2="20" y2="14"/>
      <line x1="23" y1="11" x2="17" y2="11"/>
    </svg>
  ),
  superadmin: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
    </svg>
  ),
  reports: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  )
}

const navItems = [
  { id: 'dashboard', label: 'Дашборд', icon: icons.dashboard },
  { id: 'operations', label: 'Операции', icon: icons.issued },
  { id: 'payments', label: 'Зарплаты', icon: icons.payments },
  { id: 'reports', label: 'Отчёты', icon: icons.reports },
  { id: 'schedule', label: 'График', icon: icons.schedule },
  { id: 'chat', label: 'Чаты', icon: icons.chat },
  { id: 'tasks', label: 'Задачи', icon: icons.tasks },
  { id: 'vacancies', label: 'Вакансии', icon: icons.vacancies },
  { id: 'cameras', label: 'Камеры', icon: icons.cameras },
  { id: 'ai_assistant', label: 'ИИ Ассистент', icon: icons.ai },
  { id: 'analytics', label: 'Аналитика', icon: icons.analytics },
  { id: 'support', label: 'Поддержка', icon: icons.help },
  { id: 'settings', label: 'Настройки', icon: icons.settings },
]

function PvzMultiSelect({ isSidebar = false }) {
  const { userPvzs, activePvz, setActivePvz, selectedPvzIds, setSelectedPvzIds, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  // Закрывать при клике вне компонента
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);

  const isSelected = (id) => selectedPvzIds.includes(id);

  const handleItemClick = (id) => {
    let newIds = [];
    if (isSelected(id)) {
      // Убираем из выбранных, но не даем убрать единственный выбранный ПВЗ
      if (selectedPvzIds.length > 1) {
        newIds = selectedPvzIds.filter(x => x !== id);
      } else {
        return; // Нельзя снять последнюю галочку
      }
    } else {
      newIds = [...selectedPvzIds, id];
    }
    setSelectedPvzIds(newIds);

    // Дополнительно обновляем activePvz, если он не входит в новый список
    if (activePvz && !newIds.includes(activePvz.id)) {
      const firstMatched = userPvzs.find(p => p.id === newIds[0]);
      if (firstMatched) setActivePvz(firstMatched);
    }
  };

  const handleSelectAll = () => {
    const allIds = userPvzs.map(p => p.id);
    setSelectedPvzIds(allIds);
  };

  const handleReset = () => {
    // Оставляем только первый (или основной)
    const primaryPvz = userPvzs.find(p => p.is_primary) || userPvzs[0];
    if (primaryPvz) {
      setSelectedPvzIds([primaryPvz.id]);
      setActivePvz(primaryPvz);
    }
  };

  // Формируем текст триггера
  const getTriggerText = () => {
    if (selectedPvzIds.length === 0) return 'Выберите ПВЗ';
    if (selectedPvzIds.length === userPvzs.length && userPvzs.length > 1) return `Все ПВЗ (${userPvzs.length})`;
    if (selectedPvzIds.length === 1) {
      const matched = userPvzs.find(p => p.id === selectedPvzIds[0]);
      return matched ? matched.name : 'Пункт выдачи';
    }
    return `Выбрано: ${selectedPvzIds.length} из ${userPvzs.length}`;
  };

  // Только для владельца и админа показываем мультиселект. Для остальных — статичную плашку
  const isPrivileged = profile?.role === 'owner' || profile?.role === 'admin' || profile?.role === 'admin_employee';

  if (!isPrivileged) {
    return <span className={isSidebar ? '' : 'header-logo-text'}>{activePvz?.name || 'Пункт выдачи'}</span>;
  }

  return (
    <div className={`pvz-multiselect-container ${isOpen ? 'open' : ''} ${isSidebar ? 'sidebar-pvz-multiselect' : ''}`} ref={ref}>
      <button className="pvz-multiselect-trigger" onClick={handleToggle} type="button">
        <span>{getTriggerText()}</span>
      </button>
      <span className="pvz-multiselect-chevron"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>

      {isOpen && (
        <div className="pvz-multiselect-dropdown">
          <div className="pvz-multiselect-actions">
            <button className="pvz-multiselect-btn" onClick={handleSelectAll} type="button">Выбрать все</button>
            <button className="pvz-multiselect-btn" onClick={handleReset} type="button">Сбросить</button>
          </div>
          <div className="pvz-multiselect-list">
            {userPvzs.map(p => {
              const selected = isSelected(p.id);
              return (
                <div 
                  key={p.id} 
                  className={`pvz-multiselect-item ${selected ? 'selected' : ''}`}
                  onClick={() => handleItemClick(p.id)}
                >
                  <div className="pvz-multiselect-checkbox">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <span className="pvz-multiselect-label">{p.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppLayout({ activePage, onPageChange, onLockScreen, children }) {
  const { profile, signOut, isDemoMode, userPvzs, activePvz, setActivePvz, selectedPvzIds, setSelectedPvzIds, adminPermissions: userPermissions, socket } = useAuth()
  
  const handleSwitchPvz = useCallback((val) => {
    const matched = userPvzs.find(p => p.id === val);
    if (matched) {
      setActivePvz(matched);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }, [userPvzs, setActivePvz]);

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [totalUnread, setTotalUnread] = useState(0)
  const [disputeCounts, setDisputeCounts] = useState({ payments: 0, deductions: 0, schedule: 0 })
  const [pendingRegistrationsCount, setPendingRegistrationsCount] = useState(0)
  const [unreadTasks, setUnreadTasks] = useState(0)
  const [unreadSupplies, setUnreadSupplies] = useState(0)
  const [newResponsesCount, setNewResponsesCount] = useState(0)
  const [toast, setToast] = useState(null)
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light')
  
  const handleToggleTheme = useCallback((e) => {
    const nextTheme = theme === 'light' ? 'dark' : 'light'

    const x = e.clientX || window.innerWidth / 2
    const y = e.clientY || window.innerHeight / 2
    
    document.documentElement.style.setProperty('--x', `${x}px`)
    document.documentElement.style.setProperty('--y', `${y}px`)

    if (!document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setTheme(nextTheme)
      return
    }

    document.startViewTransition(() => {
      setTheme(nextTheme)
    })
  }, [theme])

  const { startTour, isActive } = useTour()
  const [isTourSelectionOpen, setIsTourSelectionOpen] = useState(false)
  const [bottomNavVisible, setBottomNavVisible] = useState(true)

  // Viewport breakpoint state: ровно один mounted NotificationBell для текущего viewport
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false)
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const media = window.matchMedia('(max-width: 768px)')
    const updateViewport = (e) => {
      setIsMobileViewport(e.matches)
    }
    if (media.addEventListener) {
      media.addEventListener('change', updateViewport)
    } else if (media.addListener) {
      media.addListener(updateViewport)
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', updateViewport)
      } else if (media.removeListener) {
        media.removeListener(updateViewport)
      }
    }
  }, [])

  // Автозапуск мини-туров для каждого раздела отключен (запуск только вручную по кнопке ❓)

  
  // Утреннее подтверждение присутствия на ПВЗ
  const [showShiftPrompt, setShowShiftPrompt] = useState(false)
  const [shiftPromptData, setShiftPromptData] = useState(null)

  useEffect(() => {
    if (!profile?.id || profile.role === 'owner') return

    const checkShift = async () => {
      // P0-5: Не выполнять фоновые запросы проверки смены при неактивной вкладке
      if (document.hidden) return
      try {
        const res = await api.getPlannedShiftToday(profile.id)
        if (!res) return
        
        const { shift, biz, bt } = res
        
        // Показываем окно если смена запланирована (is_planned === true)
        // и смена еще НЕ начата (!shift.started_at)
        if (shift?.is_planned && !shift?.started_at) {
          const [openH, openM] = biz.open_time.split(':').map(Number)
          const nowMinutes = bt.hour * 60 + bt.minute
          const openMinutes = openH * 60 + openM
          
          // До открытия смены осталось <= 30 минут (или время открытия уже наступило/прошло)
          if (nowMinutes >= openMinutes - 30) {
            setShiftPromptData({ biz, bt })
            setShowShiftPrompt(true)
          } else {
            setShowShiftPrompt(false)
          }
        } else {
          setShowShiftPrompt(false)
        }
      } catch (err) {
        console.error('Ошибка проверки запланированной смены:', err)
      }
    }

    // Проверяем при входе и каждую минуту (только в активной вкладке)
    checkShift()
    const interval = setInterval(checkShift, 60000)

    const handleVisibility = () => {
      if (!document.hidden) {
        checkShift()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [profile?.id, profile?.role])

  const handleConfirmShift = async () => {
    if (!profile?.id || !shiftPromptData) return
    try {
      const { biz } = shiftPromptData
      // Начинаем смену принудительно со времени открытия ПВЗ (например, 09:00)
      await api.startShift(profile.id, profile.role || 'employee', biz.open_time)
      setShowShiftPrompt(false)
      
      // Генерируем событие для мгновенного обновления таймера ShiftTimer
      window.dispatchEvent(new CustomEvent('shiftStarted'))
      
      // Показываем уведомление
      setToast({ title: 'Смена открыта', message: 'Вы успешно подтвердили присутствие на ПВЗ' })
    } catch (err) {
      console.error('Ошибка подтверждения присутствия на смене:', err)
      alert(err.message || 'Не удалось открыть смену')
    }
  }
  const [showNotifBanner, setShowNotifBanner] = useState(false)
  const lastScrollY = useRef(0)
  const scrollTimer = useRef(null)


  // Register Service Worker & check notification permission
  useEffect(() => {
    // --- Fix iOS Safari keyboard bounce bug ---
    // Когда клавиатура закрывается, iOS часто оставляет всё окно (body/html) сдвинутым вверх
    const fixViewportBounce = () => {
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
      }, 50);
    };
    // document.addEventListener('focusout', fixViewportBounce);

    registerServiceWorker()
    // Show permission banner after 3 seconds if not yet asked
    const timer = setTimeout(() => {
      if (shouldAskPermission()) setShowNotifBanner(true)
    }, 3000)
    // Listen for navigateTo events from SW notification clicks
    const handleNavigate = (e) => onPageChange(e.detail)
    window.addEventListener('navigateTo', handleNavigate)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('navigateTo', handleNavigate)
      // document.removeEventListener('focusout', fixViewportBounce);
    }
  }, [])

  // Auto-resubscribe Web Push for authenticated user if permission is already granted
  const pushResubscribedRef = useRef(false)
  useEffect(() => {
    if (!profile?.id || pushResubscribedRef.current) return
    if (typeof window === 'undefined' || !('Notification' in window)) return
    if (Notification.permission !== 'granted') return

    pushResubscribedRef.current = true

    registerServiceWorker().then(async (reg) => {
      if (reg) {
        try {
          const { syncOrRepairPushSubscription } = await import('../../lib/notifications')
          await syncOrRepairPushSubscription(reg)
        } catch (e) {
          console.warn('[Notifications] Auto push resubscribe error:', e)
        }
      }
    }).catch(() => {})
  }, [profile?.id])

  // --- Auto-hide bottom nav on scroll ---
  useEffect(() => {
    const isMobile = window.innerWidth <= 768
    if (!isMobile) return

    const handleScroll = () => {
      const currentY = window.scrollY
      const delta = currentY - lastScrollY.current

      // Only trigger if scroll is significant (> 10px)
      if (Math.abs(delta) < 10) return

      if (delta > 0 && currentY > 80) {
        // Scrolling down — hide
        setBottomNavVisible(false)
      } else {
        // Scrolling up — show
        setBottomNavVisible(true)
      }

      lastScrollY.current = currentY

      // Show nav after user stops scrolling
      clearTimeout(scrollTimer.current)
      scrollTimer.current = setTimeout(() => {
        setBottomNavVisible(true)
      }, 1500)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(scrollTimer.current)
    }
  }, [])

  // --- Swipe from left edge to open sidebar ---
  const sidebarSwipeStart = useRef(null)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768
    if (!isMobile) return

    const handleTouchStart = (e) => {
      const touch = e.touches[0]
      // Only start from left edge (first 25px) and not inside chat room (where hide-bottom-nav is active)
      if (touch.clientX < 25 && !sidebarOpen && !document.body.classList.contains('hide-bottom-nav')) {
        sidebarSwipeStart.current = { x: touch.clientX, y: touch.clientY }
      }
    }

    const handleTouchEnd = (e) => {
      if (!sidebarSwipeStart.current) return
      const touch = e.changedTouches[0]
      const deltaX = touch.clientX - sidebarSwipeStart.current.x
      const deltaY = Math.abs(touch.clientY - sidebarSwipeStart.current.y)

      // Horizontal swipe > 60px, more horizontal than vertical
      if (deltaX > 60 && deltaX > deltaY) {
        setSidebarOpen(true)
      }
      sidebarSwipeStart.current = null
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [sidebarOpen])

  // --- Swipe Navigation (Clash Royale style) + Pull-to-refresh ---
  const [pullRefreshState, setPullRefreshState] = useState('idle')
  const [pullDistance, setPullDistance] = useState(0)
  
  // Swipe state machine: idle → dragging → exiting → entering → idle
  const [swipe, setSwipe] = useState({ phase: 'idle', offset: 0, dir: null, nextTab: null })
  const touchStartPos = useRef(null)
  const isHorizontalSwipe = useRef(false)
  const swipeBlockedRef = useRef(false)

  const handleExitImpersonation = () => {
    const adminToken = sessionStorage.getItem('superadmin_token');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      sessionStorage.removeItem('superadmin_token');
      localStorage.setItem('wb_active_page', 'superadmin');
      window.location.href = '/';
    }
  };

  const visibleNavItems = useMemo(() => {
    let items = navItems;

    const isSuperAdmin = profile?.email === 'sarkis.avakian20032@mail.ru' || profile?.login === 'sarkis20032';
    if (isSuperAdmin) {
      items = [...navItems, { id: 'superadmin', label: 'Админка', icon: icons.superadmin }];
    }

    if (profile?.role === 'owner') return items;

    const role = profile?.role || 'employee'
    const isManagement = ['owner', 'admin', 'admin_employee'].includes(role)

    return items.filter(item => {
      if (item.id === 'reports') {
        return isManagement
      }
      const permKey = SECTION_ACCESS_MAP[item.id]
      if (!permKey) return true
      return getPermission(userPermissions, permKey, role)
    })
  }, [profile?.role, profile?.email, profile?.login, userPermissions])

  const mobileNavItems = useMemo(() => {
    const base = visibleNavItems.slice(0, 5)
    const chatItem = visibleNavItems.find(item => item.id === 'chat')
    if (chatItem && !base.some(item => item.id === 'chat')) {
      return [...base, chatItem]
    }
    return base
  }, [visibleNavItems])

  const getAvailableTabs = useCallback(() => {
    return mobileNavItems
  }, [mobileNavItems])

  const handleTouchStart = useCallback((e) => {
    if (swipe.phase !== 'idle') return
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      scrollY: window.scrollY,
      time: Date.now()
    }
    isHorizontalSwipe.current = false
    swipeBlockedRef.current = false
    
    // Блокируем свайп страниц Clash Royale style на вкладке чата, чтобы не сбивать скролл и жесты
    if (activePage === 'chat') {
      swipeBlockedRef.current = true
    }
    
    // Блокируем свайп если палец внутри скроллируемого элемента
    if (e.target.closest('.custom-table-wrapper, .schedule-grid-wrapper, .chess-grid-wrapper, .chess-table-wrapper, .chat-messages, .chat-topics-list, .chat-new-topic, .chat-participants-selector, .fc, .chart-container, .recharts-wrapper, .react-datepicker, input, textarea, select')) {
      swipeBlockedRef.current = true
    }
    // Блокируем если внутри активного чат-рума (hide-bottom-nav означает что чат открыт)
    if (document.body.classList.contains('hide-bottom-nav')) {
      swipeBlockedRef.current = true
    }
  }, [swipe.phase, activePage])

  const handleTouchMove = useCallback((e) => {
    if (!touchStartPos.current || swipeBlockedRef.current) return
    
    const deltaX = e.touches[0].clientX - touchStartPos.current.x
    const deltaY = e.touches[0].clientY - touchStartPos.current.y
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)

    // Определяем направление при первом существенном движении
    if (!isHorizontalSwipe.current && absX > 12 && absX > absY * 1.5) {
      isHorizontalSwipe.current = true
    }
    
    if (isHorizontalSwipe.current) {
      // Игнорируем горизонтальные свайпы на мобильных, чтобы не перегружать рендеринг
      return
    }
    
    // Pull-to-refresh (только вертикальный)
    if (touchStartPos.current.scrollY <= 0 && deltaY > 0 && deltaY < 150) {
      // Снижаем частоту обновлений состояния за счет округления
      setPullDistance(Math.round(deltaY))
      setPullRefreshState('pulling')
    }
  }, [activePage, getAvailableTabs])

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartPos.current) return

    const touch = e.changedTouches[0]
    const deltaX = touch.clientX - touchStartPos.current.x
    const elapsed = Date.now() - touchStartPos.current.time
    const velocity = Math.abs(deltaX) / elapsed

    // Pull-to-refresh
    if (pullDistance > 80) {
      setPullRefreshState('refreshing')
      window.dispatchEvent(new Event('pullRefresh'))
      setTimeout(() => { setPullRefreshState('idle'); setPullDistance(0) }, 1000)
    } else {
      setPullRefreshState('idle')
      setPullDistance(0)
    }

    // Swipe navigation
    if (swipe.phase === 'dragging' && swipe.nextTab) {
      const absOffset = Math.abs(swipe.offset)
      const threshold = window.innerWidth * 0.15
      const isFlick = velocity > 0.4 && Math.abs(deltaX) > 30
      
      if (absOffset > threshold || isFlick) {
        // Порог пройден → анимируем выход текущей страницы
        const screenW = window.innerWidth
        const exitOffset = swipe.dir === 'left' ? -screenW : screenW
        const nextTabId = swipe.nextTab.id
        const exitDir = swipe.dir
        
        setSwipe(prev => ({ ...prev, phase: 'exiting', offset: exitOffset }))
        
        setTimeout(() => {
          onPageChange(nextTabId)
          // Новая страница входит с противоположной стороны
          const enterOffset = exitDir === 'left' ? screenW * 0.4 : -screenW * 0.4
          setSwipe({ phase: 'entering', offset: enterOffset, dir: exitDir, nextTab: null })
          
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setSwipe({ phase: 'idle', offset: 0, dir: null, nextTab: null })
            })
          })
        }, 180)
      } else {
        // Не достигли порога — пружиним назад
        setSwipe({ phase: 'idle', offset: 0, dir: null, nextTab: null })
      }
    } else {
      setSwipe({ phase: 'idle', offset: 0, dir: null, nextTab: null })
    }

    touchStartPos.current = null
    isHorizontalSwipe.current = false
  }, [pullDistance, swipe, onPageChange])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])

  // ─── UX-Трекер: инициализация и отслеживание страниц ──────
  useEffect(() => {
    if (profile?.id) {
      initTracker()
      return () => stopTracker()
    }
  }, [profile?.id])

  useEffect(() => {
    if (profile?.id && activePage) {
      trackPageView(activePage)
    }
  }, [activePage, profile?.id])

  // Автоматическое скрытие тоста через 5 секунд
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Определение Ozon/WB контекста
  useEffect(() => {
    if (activePvz?.platform === 'ozon') {
      document.documentElement.setAttribute('data-brand', 'ozon')
    } else {
      document.documentElement.removeAttribute('data-brand')
    }
  }, [activePvz?.platform])

  // Централизованный метод загрузки счетчиков
  const loadCounts = useCallback(() => {
    if (!profile?.id) return
    
    // Не делаем запросы когда вкладка в фоне
    if (document.hidden) return

    api.getUnreadCounts(profile.id)
      .then(res => {
        const counts = res?.counts || {}
        const total = Object.values(counts).reduce((s, c) => s + c, 0)
        setTotalUnread(total)
      })
      .catch(() => {})

    api.getAllNotificationCounts(profile.id, profile.role).then(nc => {
      if (profile.role === 'owner' || profile.role === 'admin' || profile.role === 'admin_employee') {
        const counts = {
          payments: nc.disputes_payments || 0,
          deductions: nc.disputes_deductions || 0,
          schedule: nc.schedule_pending || 0
        }
        setDisputeCounts(counts)
        setPendingRegistrationsCount(nc.pending_users || 0)
        const evt = new CustomEvent('disputeCountsUpdated', { detail: counts })
        window.dispatchEvent(evt)
      }
    }).catch(() => {})

    // Загрузка задач
    if (profile.org_id && activePage !== 'tasks') {
      api.getTasks(profile.org_id).then(res => {
        const activeTasks = (res || []).filter(t => t.assigned_to === profile.id && ['new', 'in_progress'].includes(t.status))
        setUnreadTasks(activeTasks.length)
      }).catch(() => {})
    }

    // Загрузка хознужд (для владельца / админов)
    const isPrivileged = ['owner', 'admin', 'admin_employee'].includes(profile.role)
    if (isPrivileged && profile.org_id && activePage !== 'operations') {
      const getCountPromise = profile.role === 'owner' 
        ? api.getOwnerActiveSuppliesCount(profile.org_id, activePvz?.id)
        : api.getPendingSupplyRequestsCount(profile.org_id, activePvz?.id)
      
      getCountPromise.then(count => {
        setUnreadSupplies(count)
      }).catch(() => {})
    }

    // Загрузка новых откликов на вакансии (для владельца / админов)
    if (isPrivileged && profile.org_id && activePage !== 'vacancies') {
      api.getNewResponsesCount(profile.org_id)
        .then(count => {
          setNewResponsesCount(count)
        })
        .catch(() => {})
    }
  }, [profile?.id, profile?.role, profile?.org_id, activePvz?.id, activePage])

  // P0-4: Изолированное обновление только счетчика чатов без каскада на остальные 5 эндпоинтов
  const refreshChatUnreadCount = useCallback(() => {
    if (!profile?.id || document.hidden) return
    api.getUnreadCounts(profile.id)
      .then(res => {
        const counts = res?.counts || {}
        const total = Object.values(counts).reduce((s, c) => s + c, 0)
        setTotalUnread(total)
      })
      .catch(() => {})
  }, [profile?.id])

  // Первоначальная загрузка и опрос (как fallback)
  useEffect(() => {
    if (profile?.id) {
      loadCounts()

      const interval = setInterval(loadCounts, 60000)

      // Throttle: не чаще чем раз в 5 секунд при visibilitychange
      let lastLoadTime = Date.now()
      const handleVisibility = () => {
        if (!document.hidden) {
          const now = Date.now()
          if (now - lastLoadTime >= 5000) {
            lastLoadTime = now
            loadCounts()
          }
        }
      }
      document.addEventListener('visibilitychange', handleVisibility)

      const handleChatRead = () => refreshChatUnreadCount()
      window.addEventListener('chatRead', handleChatRead)
      window.addEventListener('disputesUpdated', loadCounts)

      return () => {
        clearInterval(interval)
        document.removeEventListener('visibilitychange', handleVisibility)
        window.removeEventListener('chatRead', handleChatRead)
        window.removeEventListener('disputesUpdated', loadCounts)
      }
    }
  }, [profile?.id, loadCounts, refreshChatUnreadCount])

  // Обновление счетчиков по кастомному событию
  useEffect(() => {
    const handleUpdateCounts = () => {
      loadCounts()
    }
    window.addEventListener('updateNotificationCounts', handleUpdateCounts)
    return () => window.removeEventListener('updateNotificationCounts', handleUpdateCounts)
  }, [loadCounts])

  // WebSocket подписки — Socket.io
  useEffect(() => {
    if (!profile?.id || !socket) return

    const playSound = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        ctx.resume().then(() => {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.type = 'sine'
          osc.frequency.setValueAtTime(880, ctx.currentTime)
          osc.frequency.setValueAtTime(660, ctx.currentTime + 0.1)
          gain.gain.setValueAtTime(0.3, ctx.currentTime)
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
          osc.start(ctx.currentTime)
          osc.stop(ctx.currentTime + 0.3)
        }).catch(() => {})
      } catch (e) { /* audio not available */ }
    }

    // Сообщения в каналах
    const handleNewChannelMessage = (msg) => {
      // Сохраняем совместимость с ChatPage
      window.dispatchEvent(new CustomEvent('supabase-new-channel-message', { 
        detail: msg 
      }))

      if (msg.author_id === profile.id) return // Свои сообщения
      if (isChatMuted(msg.topic_id)) return

      playSound()
      const title = msg.author_name || 'Коллега'
      setToast({ 
        title, 
        message: msg.content?.slice(0, 80) || 'Новое сообщение', 
        targetType: 'channel', 
        targetId: msg.topic_id 
      })
      showBrowserNotification(title, {
        body: msg.content?.slice(0, 120) || 'Новое сообщение в чате',
        tag: 'chat-channel-' + msg.topic_id,
        navType: 'chat',
        targetType: 'channel',
        targetId: msg.topic_id,
        onClick: () => {
          localStorage.setItem('wb_chat_navigate', JSON.stringify({ type: 'channel', id: msg.topic_id }))
          onPageChange('chat')
        }
      })
      loadCounts()
    }

    // Личные сообщения
    const handleNewDirectMessage = (msg) => {
      const senderName = msg.sender_name || msg.sender?.full_name || 'Коллега'
      
      // Сохраняем совместимость с ChatPage
      window.dispatchEvent(new CustomEvent('supabase-new-direct-message', { 
        detail: {
          ...msg,
          sender: msg.sender || { full_name: senderName, avatar_url: msg.sender_avatar }
        } 
      }))

      if (msg.sender_id === profile.id) return

      if (isChatMuted(msg.room_id)) return

      playSound()
      setToast({ 
        title: senderName, 
        message: msg.content?.slice(0, 80) || 'Личное сообщение', 
        targetType: 'direct', 
        targetId: msg.room_id 
      })
      showBrowserNotification(senderName, {
        body: msg.content?.slice(0, 120) || 'Новое личное сообщение',
        tag: 'chat-dm-' + msg.room_id,
        navType: 'chat',
        targetType: 'direct',
        targetId: msg.room_id,
        onClick: () => {
          localStorage.setItem('wb_chat_navigate', JSON.stringify({ type: 'direct', id: msg.room_id }))
          onPageChange('chat')
        }
      })
      loadCounts()
    }

    // Оспаривания
    const handleDisputeUpdated = (dispute) => {
      playSound()
      const isOwnerOrAdmin = ['owner', 'admin', 'admin_employee'].includes(profile.role)
      
      if (isOwnerOrAdmin) {
        setToast({ title: 'Оспаривание', message: 'Сотрудник оспорил начисление' })
      } else {
        setToast({ title: 'Спор разрешен', message: 'Владелец вынес решение по спору' })
      }
      
      window.dispatchEvent(new Event('disputesUpdated'))
      window.dispatchEvent(new CustomEvent('payment-updated', { detail: dispute }))
      loadCounts()
    }

    // Системные уведомления (выплаты, штрафы, премии, отклики на вакансии)
    const handleNotification = (notif) => {
      playSound()
      setToast({ title: notif.title || 'Новое уведомление', message: notif.message || '' })
      
      const isVacancyResponse = notif.type === 'vacancy_response'
      const targetPage = isVacancyResponse ? 'vacancies' : 'payments'
      
      showBrowserNotification(notif.title || 'Новое уведомление', {
        body: notif.message || '',
        tag: 'notification-' + (notif.id || Date.now()),
        navType: targetPage,
        onClick: () => {
          onPageChange(targetPage)
          if (isVacancyResponse) {
            window.dispatchEvent(new CustomEvent('vacancies-responses-updated'))
          }
        }
      })
      loadCounts()
      if (isVacancyResponse) {
        window.dispatchEvent(new CustomEvent('vacancies-responses-updated'))
      } else {
        window.dispatchEvent(new CustomEvent('payment-updated', { detail: notif }))
      }
    }

    // Задачи
    const handleTaskUpdated = (task) => {
      loadCounts()
      window.dispatchEvent(new CustomEvent('tasks-changed', { detail: task }))
    }

    // Хознужды
    const handleSupplyRequestUpdated = (req) => {
      loadCounts()
      window.dispatchEvent(new CustomEvent('supply-requests-changed', { detail: req }))
    }

    // Подписываемся на события сокета
    socket.on('new-channel-message', handleNewChannelMessage)
    socket.on('new-direct-message', handleNewDirectMessage)
    socket.on('dispute-updated', handleDisputeUpdated)
    socket.on('notification', handleNotification)
    socket.on('task-updated', handleTaskUpdated)
    socket.on('supply-request-updated', handleSupplyRequestUpdated)
    
    // Любые другие события обновления оплаты
    socket.on('payment-updated', (data) => {
      window.dispatchEvent(new CustomEvent('payment-updated', { detail: data }))
      loadCounts()
    })

      // Новая регистрация сотрудника
      const handleNewRegistration = (data) => {
        console.log('[WS] New registration in AppLayout:', data)
        if (profile?.role === 'owner') {
          playSound()
          setToast({
            title: 'Новая заявка',
            message: `${data.full_name || 'Сотрудник'} отправил заявку на регистрацию`
          })
          showBrowserNotification('Новая заявка на вступление', {
            body: `${data.full_name || 'Сотрудник'} отправил заявку в вашу команду`,
            tag: 'new-registration-' + (data.id || Date.now()),
            navType: 'settings',
            onClick: () => {
              onPageChange('settings', { tab: 'team' })
            }
          })
          setPendingRegistrationsCount(prev => prev + 1)
          window.dispatchEvent(new CustomEvent('new-registration-received', { detail: data }))
        }
      }

      socket.on('new-registration', handleNewRegistration)

      const handleVacanciesResponsesUpdated = () => {
        loadCounts()
        window.dispatchEvent(new CustomEvent('vacancies-responses-updated'))
      }
      socket.on('vacancies-responses-updated', handleVacanciesResponsesUpdated)

      return () => {
        socket.off('new-channel-message', handleNewChannelMessage)
        socket.off('new-direct-message', handleNewDirectMessage)
        socket.off('dispute-updated', handleDisputeUpdated)
        socket.off('notification', handleNotification)
        socket.off('task-updated', handleTaskUpdated)
        socket.off('supply-request-updated', handleSupplyRequestUpdated)
        socket.off('new-registration', handleNewRegistration)
        socket.off('vacancies-responses-updated', handleVacanciesResponsesUpdated)
        socket.off('payment-updated')
      }
  }, [profile?.id, profile?.role, socket])

  // Сбрасываем badge при открытии страницы задач
  useEffect(() => {
    if (activePage === 'tasks') {
      setUnreadTasks(0)
    }
  }, [activePage])

  // Сбрасываем badge при открытии страницы вакансий
  useEffect(() => {
    if (activePage === 'vacancies') {
      setNewResponsesCount(0)
    }
  }, [activePage])

  // Сбрасываем badge при открытии страницы операций
  useEffect(() => {
    if (activePage === 'operations') {
      setUnreadSupplies(0)
    }
  }, [activePage])

  // Сбрасываем скролл при смене страницы (предотвращает залипание прокрутки)
  useEffect(() => {
    const mainEl = document.querySelector('.app-content')
    if (mainEl) {
      mainEl.scrollTop = 0
    }
    window.scrollTo(0, 0)
  }, [activePage])

  useEffect(() => {
    const isPrivileged = ['owner', 'admin', 'admin_employee'].includes(profile?.role)
    if (!isPrivileged) return

    const loadSupplyBadge = async () => {
      try {
        let count = 0
        if (profile.role === 'owner') {
          count = await api.getOwnerActiveSuppliesCount(profile.org_id, activePvz?.id)
        } else {
          count = await api.getPendingSupplyRequestsCount(profile.org_id, activePvz?.id)
        }
        setUnreadSupplies(count)
      } catch (e) { console.warn('Supply badge error:', e) }
    }
    loadSupplyBadge()

    const supplyChannel = supabase
      .channel('rt-supply-requests-badge')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'supply_requests',
        filter: `org_id=eq.${profile.org_id}`
      }, (payload) => {
        const req = payload.new
        // Если заявку создал другой человек, проигрываем звук и шлем тост
        if (req.employee_id !== profile.id) {
          playSound()
          setToast({ title: 'Новая заявка на хознужды', message: req.title?.slice(0, 80) || '' })
          showBrowserNotification('Новая заявка на хознужды', {
            body: `Сотрудник создал новую заявку: ${req.title}`,
            tag: 'supply-rt-' + req.id,
            navType: 'operations',
            onClick: () => {
              try { localStorage.setItem('default_operations_tab', 'supplies') } catch (e) {}
              onPageChange('operations')
            }
          })
        }
        api.clearCache('supply_requests')
        window.dispatchEvent(new CustomEvent('supply-requests-changed'))
        loadSupplyBadge()
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'supply_requests',
        filter: `org_id=eq.${profile.org_id}`
      }, (payload) => {
        api.clearCache('supply_requests')
        window.dispatchEvent(new CustomEvent('supply-requests-changed'))
        loadSupplyBadge()
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'supply_requests',
        filter: `org_id=eq.${profile.org_id}`
      }, () => {
        api.clearCache('supply_requests')
        window.dispatchEvent(new CustomEvent('supply-requests-changed'))
        loadSupplyBadge()
      })
      .subscribe()

    return () => {
      supabase.removeChannel(supplyChannel)
    }
  }, [profile?.id, profile?.org_id, activePvz?.id])

  // Сбрасываем badge при открытии страницы операций
  useEffect(() => {
    if (activePage === 'operations') {
      setUnreadSupplies(0)
    }
  }, [activePage])

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner': return { text: 'Владелец', class: 'role-owner' }
      case 'admin': return { text: 'Админ', class: 'role-admin' }
      case 'admin_employee': return { text: 'Админ-сотрудник', class: 'role-admin-employee' }
      case 'helper': return { text: 'Помощник', class: 'role-helper' }
      default: return { text: 'Сотрудник', class: 'role-employee' }
    }
  }

  const role = getRoleBadge(profile?.role)

  const isImpersonating = !!sessionStorage.getItem('superadmin_token');

  const getSidebarTitle = () => {
    if (!selectedPvzIds || selectedPvzIds.length === 0) return 'Выберите ПВЗ';
    if (selectedPvzIds.length === userPvzs?.length && userPvzs?.length > 1) {
      return `Все ПВЗ (${userPvzs.length})`;
    }
    if (selectedPvzIds.length === 1) {
      const matched = userPvzs?.find(p => p.id === selectedPvzIds[0]);
      return matched ? matched.name : 'Пункт выдачи';
    }
    return `Выбрано: ${selectedPvzIds.length} ПВЗ`;
  };

  const getSidebarSubtitle = () => {
    if (!selectedPvzIds || selectedPvzIds.length === 0) return '';
    const selectedPvzs = userPvzs?.filter(p => selectedPvzIds.includes(p.id)) || [];
    const platforms = [...new Set(selectedPvzs.map(p => p.platform === 'ozon' ? 'Ozon' : 'Wildberries'))];
    return platforms.join(' / ');
  };

  return (
    <div className={`app-layout ${activePage === 'chat' ? 'app-layout-chat' : ''} ${isImpersonating ? 'has-impersonation' : ''}`}>
      {isImpersonating && (
        <div className="impersonation-banner">
          <div className="impersonation-banner-content">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', verticalAlign: 'middle'}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span>Режим имитации: <strong>{profile?.full_name}</strong></span>
          </div>
          <button className="impersonation-exit-btn" onClick={handleExitImpersonation}>
            Выйти
          </button>
        </div>
      )}
      {toast && (
        <div className="chat-toast" onClick={() => {
          if (toast.targetType && toast.targetId) {
            localStorage.setItem('wb_chat_navigate', JSON.stringify({ type: toast.targetType, id: toast.targetId }))
          }
          onPageChange('chat')
          setToast(null)
        }}>
          <div className="chat-toast-icon"></div>
          <div className="chat-toast-content">
            <div className="chat-toast-title">{toast.title}</div>
            <div className="chat-toast-message">{toast.message}</div>
          </div>
          <button className="chat-toast-close" onClick={(e) => { e.stopPropagation(); setToast(null); }}></button>
        </div>
      )}

      {/* Notification Permission Banner */}
      {showNotifBanner && (
        <div style={{
          position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 10000, maxWidth: '400px', width: 'calc(100% - 32px)',
          background: 'var(--bg-primary)', border: '1px solid var(--surface-border)',
          borderRadius: '16px', padding: '16px 20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          animation: 'fadeSlideDown 0.3s ease-out',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '28px' }}></span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>Включить уведомления?</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>Узнавайте о новых сообщениях даже когда вкладка в фоне</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={async () => {
              await requestNotificationPermission()
              setShowNotifBanner(false)
            }} style={{
              flex: 1, padding: '10px', border: 'none', borderRadius: '10px', cursor: 'pointer',
              background: 'linear-gradient(135deg, var(--wb-primary), #9333ea)', color: 'white', fontWeight: 600, fontSize: '13px'
            }}>Включить</button>
            <button onClick={() => {
              localStorage.setItem('pvz_notification_asked', 'true')
              setShowNotifBanner(false)
            }} style={{
              flex: 1, padding: '10px', border: '1px solid var(--surface-border)', borderRadius: '10px', cursor: 'pointer',
              background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '13px'
            }}>Не сейчас</button>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      <header className="app-header">
        <button
          className="btn btn-icon btn-ghost header-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Меню"
        >
          {icons.menu}
        </button>

        <div className="header-title" data-tour="pvz-select">
          {userPvzs?.length > 1 ? (
            <PvzMultiSelect isSidebar={false} />
          ) : (
            <span className="header-logo-text">{activePvz?.name || 'Пункт выдачи'}</span>
          )}
          {isDemoMode && <span className="badge badge-warning" style={{ fontSize: '9px', marginLeft: '6px' }}>ДЕМО</span>}
        </div>

        <div className="header-user">
          {isMobileViewport && (
            <NotificationBell
              profile={profile}
              socket={socket}
              onNavigate={onPageChange}
            />
          )}
          <button 
            className="btn btn-icon btn-ghost" 
            style={{ color: 'var(--wb-primary)' }}
            onClick={() => { setIsTourSelectionOpen(true); setSidebarOpen(false) }}
            title="Обучение"
          >
            {icons.help}
          </button>
          <button 
            className="btn btn-icon btn-ghost" 
            style={{ }}
            onClick={handleToggleTheme}
          >
            {theme === 'light' ? icons.moon : icons.sun}
          </button>
          <div className="avatar avatar-sm">
            {profile?.avatar_url ? (
              <img src={proxyStorageUrl(profile.avatar_url)} alt={profile.full_name} />
            ) : (
              getInitials(profile?.full_name)
            )}
          </div>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <LogoIcon size={52} style={{ borderRadius: '12px' }} />
            </div>
            <div>
              <h2>{getSidebarTitle()}</h2>
              <p className="sidebar-version">{getSidebarSubtitle()}</p>
            </div>
          </div>
          
          {userPvzs?.length > 1 && (
            <div style={{ marginTop: '16px', padding: '0 8px' }} data-tour="pvz-select">
              <PvzMultiSelect isSidebar={true} />
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="sidebar-user">
          <div className="avatar">
            {profile?.avatar_url ? (
              <img src={proxyStorageUrl(profile.avatar_url)} alt={profile.full_name} />
            ) : (
              getInitials(profile?.full_name)
            )}
          </div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{profile?.full_name}</span>
            <span className={`sidebar-user-role ${role.class}`}>{role.text}</span>
          </div>
          {!isMobileViewport && (
            <div className="sidebar-user-notification desktop-notification-bell">
              <NotificationBell
                profile={profile}
                socket={socket}
                onNavigate={onPageChange}
              />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {visibleNavItems.map((item) => (
            <React.Fragment key={item.id}>
              {item.id === 'support' && (
                <div style={{
                  margin: '12px 12px 4px 12px',
                  borderTop: '1px solid var(--border-color, rgba(0, 0, 0, 0.08))',
                  paddingTop: '10px',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: 'var(--text-secondary, #64748b)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px'
                }}>
                  ПОМОЩЬ
                </div>
              )}
              <button
                className={`sidebar-nav-item ${activePage === item.id ? 'active' : ''}`}
                data-tour={`${item.id}-menu`}
                onClick={() => {
                  if (item.id === 'operations' && unreadSupplies > 0) {
                    try { localStorage.setItem('default_operations_tab', 'supplies') } catch (e) {}
                  }
                  if (item.id === 'settings' && pendingRegistrationsCount > 0) {
                    onPageChange(item.id, { tab: 'team' })
                  } else {
                    onPageChange(item.id)
                  }
                  setSidebarOpen(false)
                }}
              >
                <span className="sidebar-nav-icon">{item.icon}</span>
                <span className="sidebar-nav-label">
                  {(item.id === 'operations' && profile?.role === 'employee') ? 'Рабочая смена' : item.label}
                </span>
                {item.id === 'chat' && totalUnread > 0 && (
                  <span className="badge badge-danger sidebar-nav-badge">{totalUnread}</span>
                )}
                {item.id === 'payments' && (disputeCounts.payments > 0 || disputeCounts.deductions > 0) && (
                  <span className="badge sidebar-nav-badge" style={{ background: 'orange', color: 'white' }}>
                    {disputeCounts.payments + disputeCounts.deductions}
                  </span>
                )}
                {item.id === 'schedule' && disputeCounts.schedule > 0 && (
                  <span className="badge sidebar-nav-badge" style={{ background: 'var(--warning)', color: 'white' }}>
                    {disputeCounts.schedule}
                  </span>
                )}
                {item.id === 'tasks' && unreadTasks > 0 && activePage !== 'tasks' && (
                  <span className="badge badge-danger sidebar-nav-badge">{unreadTasks}</span>
                )}
                {item.id === 'operations' && unreadSupplies > 0 && activePage !== 'operations' && (
                  profile?.role === 'owner' ? (
                    <span className="badge badge-danger sidebar-nav-badge animate-pulse" style={{ fontSize: '13px', fontWeight: 900, background: 'var(--danger)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 1s infinite' }}>!</span>
                  ) : (
                    <span className="badge badge-danger sidebar-nav-badge">{unreadSupplies}</span>
                  )
                )}
                {item.id === 'vacancies' && newResponsesCount > 0 && activePage !== 'vacancies' && (
                  <span className="badge badge-danger sidebar-nav-badge">{newResponsesCount}</span>
                )}
                {item.id === 'settings' && pendingRegistrationsCount > 0 && (
                  <span className="badge badge-danger sidebar-nav-badge animate-pulse" style={{ background: 'var(--danger)', color: 'white' }}>
                    {pendingRegistrationsCount}
                  </span>
                )}
              </button>
            </React.Fragment>
          ))}
        </nav>

        {/* Footer (Theme & Logout) */}
        <div className="sidebar-footer">
          <button 
            className="sidebar-nav-item" 
            onClick={() => { window.location.href = '/site'; setSidebarOpen(false) }}
            style={{ color: 'var(--text-secondary)' }}
          >
            <span className="sidebar-nav-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </span>
            <span className="sidebar-nav-label">О сервисе</span>
          </button>
          <button 
            className="sidebar-nav-item" 
            onClick={() => { setIsTourSelectionOpen(true); setSidebarOpen(false) }}
            style={{ color: 'var(--wb-primary)' }}
          >
            <span className="sidebar-nav-icon">{icons.help}</span>
            <span className="sidebar-nav-label">Обучение</span>
          </button>
          <button 
            className="sidebar-nav-item" 
            onClick={handleToggleTheme}
          >
            <span className="sidebar-nav-icon">{theme === 'light' ? icons.moon : icons.sun}</span>
            <span className="sidebar-nav-label">{theme === 'light' ? 'Темная тема' : 'Светлая тема'}</span>
          </button>
          {onLockScreen && (
            <button className="sidebar-nav-item sidebar-switch-user" onClick={onLockScreen}>
              <span className="sidebar-nav-icon">{icons.switchUser}</span>
              <span className="sidebar-nav-label">Сменить пользователя</span>
            </button>
          )}
          <button className="sidebar-nav-item sidebar-logout" onClick={signOut}>
            <span className="sidebar-nav-icon">{icons.logout}</span>
            <span className="sidebar-nav-label">Выйти</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`app-content page-${activePage}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ overflow: swipe.phase !== 'idle' ? 'hidden' : undefined, position: 'relative' }}
      >
        {/* Pull-to-refresh indicator */}
        {pullRefreshState !== 'idle' && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            height: pullRefreshState === 'refreshing' ? 40 : Math.min(pullDistance * 0.5, 40),
            overflow: 'hidden', transition: pullRefreshState === 'refreshing' ? 'height 0.3s' : 'none',
            color: 'var(--text-secondary)', fontSize: '13px', gap: '8px',
          }}>
            <span style={{
              display: 'inline-block',
              transform: pullRefreshState === 'refreshing' ? 'none' : `rotate(${Math.min(pullDistance * 3, 360)}deg)`,
              animation: pullRefreshState === 'refreshing' ? 'spin 0.8s linear infinite' : 'none',
            }}>↻</span>
            {pullRefreshState === 'refreshing' ? 'Обновление...' : pullDistance > 80 ? 'Отпустите' : 'Потяните вниз'}
          </div>
        )}

        {/* Swipeable page wrapper */}
        <div
          key={activePage}
          className="animate-page-transition"
          style={{
            // transform: none в idle — чтобы не создавать stacking context для fixed-элементов
            transform: swipe.offset ? `translateX(${swipe.offset}px)` : undefined,
            transition: swipe.phase === 'dragging' ? 'none' 
              : swipe.phase === 'exiting' ? 'transform 180ms cubic-bezier(0.2, 0, 0, 1)'
              : swipe.phase === 'entering' ? 'transform 250ms cubic-bezier(0, 0, 0.2, 1)'
              : undefined,
            willChange: swipe.phase !== 'idle' ? 'transform' : undefined,
            minHeight: '100%',
            height: activePage === 'chat' ? '100%' : undefined,
            maxHeight: activePage === 'chat' ? '100%' : undefined,
            width: activePage === 'chat' ? '100%' : undefined,
            maxWidth: activePage === 'chat' ? '100%' : undefined,
            display: activePage === 'chat' ? 'flex' : undefined,
            flexDirection: activePage === 'chat' ? 'column' : undefined,
            overflow: activePage === 'chat' ? 'hidden' : undefined,
          }}
        >
          {children}
        </div>

        {/* Спейсер убран — padding-bottom на .app-content компенсирует высоту навбара */}

        {/* Peek preview of next tab during swipe */}
        {swipe.phase === 'dragging' && swipe.nextTab && (
          <div style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            width: '100%',
            [swipe.dir === 'left' ? 'right' : 'left']: 0,
            transform: `translateX(${swipe.dir === 'left' ? `calc(100% + ${swipe.offset}px)` : `calc(-100% + ${swipe.offset}px)`})`,
            background: 'var(--bg-primary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            zIndex: 1,
            pointerEvents: 'none',
            opacity: Math.min(Math.abs(swipe.offset) / 80, 0.8),
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: 'var(--surface)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: 'var(--surface-shadow)',
              color: 'var(--wb-primary)',
            }}>
              {swipe.nextTab.icon}
            </div>
            <span style={{
              fontSize: '15px', fontWeight: 600,
              color: 'var(--text-primary)',
            }}>
              {swipe.nextTab.label}
            </span>
          </div>
        )}
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className={`bottom-nav ${bottomNavVisible ? '' : 'bottom-nav-hidden'}`}>
        {mobileNavItems.map((item) => (
          <button
            key={item.id}
            className={`bottom-nav-item ${activePage === item.id ? 'active' : ''}`}
            data-tour={`${item.id}-menu`}
            onClick={() => {
              if (item.id === 'settings' && pendingRegistrationsCount > 0) {
                onPageChange(item.id, { tab: 'team' })
              } else {
                onPageChange(item.id)
              }
            }}
          >
            <span className="bottom-nav-icon">
              {item.icon}
              {item.id === 'chat' && totalUnread > 0 && (
                <span className="bottom-nav-badge">{totalUnread}</span>
              )}
              {item.id === 'payments' && (disputeCounts.payments > 0 || disputeCounts.deductions > 0) && (
                <span className="bottom-nav-badge" style={{ background: 'orange', color: 'white' }}>
                  {disputeCounts.payments + disputeCounts.deductions}
                </span>
              )}
              {item.id === 'tasks' && unreadTasks > 0 && activePage !== 'tasks' && (
                <span className="bottom-nav-badge">{unreadTasks}</span>
              )}
              {item.id === 'operations' && unreadSupplies > 0 && activePage !== 'operations' && (
                profile?.role === 'owner' ? (
                  <span className="bottom-nav-badge animate-pulse" style={{ background: 'var(--danger)', color: 'white', animation: 'pulse 1s infinite', fontSize: '11px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px' }}>!</span>
                ) : (
                  <span className="bottom-nav-badge">{unreadSupplies}</span>
                )
              )}
              {item.id === 'vacancies' && newResponsesCount > 0 && activePage !== 'vacancies' && (
                <span className="bottom-nav-badge">{newResponsesCount}</span>
              )}
              {item.id === 'settings' && pendingRegistrationsCount > 0 && (
                <span className="bottom-nav-badge" style={{ background: 'var(--danger)', color: 'white' }}>{pendingRegistrationsCount}</span>
              )}
            </span>
            <span className="bottom-nav-label">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Guided Tour теперь управляется через TourContext + TourGuide в App.jsx */}

      {/* Модальное окно подтверждения смены (блокирующее) */}
      <Modal 
        isOpen={showShiftPrompt} 
        onClose={() => {}} 
        title="Подтверждение присутствия" 
        isBottomSheet={true}
      >
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px', color: 'var(--text-primary)' }}>
            Вы на смене?
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '24px' }}>
            Сегодня по графику у вас запланирована смена в ПВЗ. Подтвердите своё присутствие на рабочем месте.
          </p>
          <button 
            className="btn" 
            style={{ 
              width: '100%', 
              background: 'linear-gradient(135deg, var(--wb-primary), #7c3aed)', 
              color: '#fff', 
              fontWeight: 700, 
              padding: '14px', 
              borderRadius: '12px',
              fontSize: '16px',
              border: 'none',
              boxShadow: '0 4px 15px rgba(203,17,171,0.3)',
              cursor: 'pointer'
            }}
            onClick={handleConfirmShift}
          >
            Да, я на смене
          </button>
        </div>
      </Modal>
      <TutorialModal 
        isOpen={isTourSelectionOpen} 
        onClose={() => setIsTourSelectionOpen(false)} 
        activePage={activePage} 
      />
    </div>
  )
}
